import { useId, useMemo, useRef, useState } from 'react';
import {
  AlertTriangleIcon,
  CheckIcon,
  ChevronRightIcon,
  PlusIcon,
  SearchIcon,
  XIcon,
} from 'lucide-react';
import { leesbareFout, vindNamen } from '@vip/shared';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardAction,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Empty, EmptyHeader, EmptyTitle } from '@/components/ui/empty';
import { Input } from '@/components/ui/input';
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemGroup,
  ItemTitle,
} from '@/components/ui/item';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { ApiError } from '../../lib/apiClient';
import {
  useGesprek,
  useHulpvragen,
  useStelHulpvragenVast,
  useVerwijderHulpvraag,
  useVoegHulpvraagToe,
  useWerkHulpvraagBij,
  useZoekHulpvragen,
  type HulpvraagRij,
} from '../gesprekken/gesprekken.api';
import { BezigMetZoeken } from './BezigMetZoeken';
import { bestaandeSpelling, bouwGroepen, type Groep, type VraagVelden } from './groeperen';

/**
 * Scherm 2: de hulpvragen uit dit gesprek.
 *
 * Geordend per hoofdonderwerp, met daaronder wat er precies aan schortte. Die
 * twee lagen zijn de reden dat dit optelt: twaalf mensen met een SAP-vraag
 * worden één regel op het dashboard in plaats van twaalf formuleringen.
 *
 * Alles wat er staat is te wijzigen, mag weg, en de buddy kan er zelf bij
 * zetten. Het model hoort een term verkeerd ("Cloud" waar "Claude" stond) en dat
 * is met één woord recht te zetten; opnieuw laten zoeken kost een call en levert
 * dezelfde vergissing op.
 *
 * De velden bewaren niet per toetsaanslag en niet per regel. Wat je typt staat in
 * een klad, en vaststellen schrijft het in één keer weg. Er is geen
 * "accepteer alles"-knop: vaststellen is een handeling van een mens, en pas
 * daarbij ontstaan de rijen in de taxonomie (§6.3).
 */
export function HulpvraagReview() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { data: gesprek } = useGesprek(id);
  const status = gesprek?.meeting.status;
  // Ook CONFIRMED: na het goedkeuren start de server het zoeken zelf, desnoods
  // via de bewaker.
  const bezig = status === 'EXTRACTING' || status === 'CONFIRMED';

  const { data, isLoading } = useHulpvragen(id, status);
  const zoek = useZoekHulpvragen(id);
  const verwijder = useVerwijderHulpvraag(id);
  const werkBij = useWerkHulpvraagBij(id);
  const voegToe = useVoegHulpvraagToe(id);
  const stelVast = useStelHulpvragenVast(id);
  const [fout, setFout] = useState('');
  const [samenvattingOpen, setSamenvattingOpen] = useState(false);
  /** Wat de buddy heeft getypt, per hulpvraag. Alleen de regels waar hij aan
   *  heeft gezeten staan erin; de rest komt rechtstreeks van de server, zodat
   *  een verversing niets overschrijft. */
  const [klad, setKlad] = useState<Record<string, VraagVelden>>({});
  /** Wat de buddy er zelf bij zet. Staat naast het klad omdat deze regels nog
   *  geen id op de server hebben; ze ontstaan pas bij het vaststellen. */
  const [nieuwe, setNieuwe] = useState<Array<{ sleutel: number; velden: VraagVelden }>>([]);
  const teller = useRef(0);

  // Via useMemo omdat `?? []` bij elke render een nieuwe array is, en die hangt
  // onder de memo van de groepering.
  const hulpvragen = useMemo(() => data?.hulpvragen ?? [], [data]);
  const alVastgesteld =
    status === 'DONE' || (hulpvragen.length > 0 && hulpvragen.every((h) => h.confirmedAt));
  /** De onderwerpen die al zijn vastgesteld. Hieraan wordt afgemeten of een
   *  onderwerp nieuw is, en dus niet aan de namen uit dit gesprek: een onderwerp
   *  dat het model hier voor het eerst vond bestaat nog niet. */
  const taxonomie = useMemo(() => (data?.taxonomie ?? []).map((t) => t.naam), [data]);
  /** Waar een naam op mag aansluiten: de onderwerpen van dít gesprek eerst, want
   *  wie er zelf een hulpvraag bij zet hangt hem vaker onder iets dat net is
   *  gevonden dan onder een naam uit de hele taxonomie. Dit is ook de lijst
   *  waarop wordt samengevoegd. */
  const bekend = useMemo(
    () => [...new Set([...hulpvragen.map((h) => h.onderwerpNaam), ...taxonomie])],
    [hulpvragen, taxonomie],
  );
  // Gevonden en zelf toegevoegde regels door één molen, gegroepeerd op de naam
  // uit het klad. Zo komt een hulpvraag die je onder een ander onderwerp hangt
  // daar ook werkelijk terecht, en staat een regel die je onder een bestaand
  // onderwerp aanmaakt meteen op de juiste kaart.
  const groepen = useMemo(
    () => bouwGroepen(hulpvragen, nieuwe, klad, taxonomie),
    [hulpvragen, nieuwe, klad, taxonomie],
  );

  const velden = (h: HulpvraagRij): VraagVelden => klad[h.id] ?? vanServer(h);
  // De basis komt uit de updater en niet uit `klad` van deze render: het wijzigen
  // van een onderwerp zet alle regels van de groep in één gebeurtenis, en dan is
  // de tweede setState met een verouderde kopie aan het rekenen.
  const zet = (h: HulpvraagRij, deel: Partial<VraagVelden>) =>
    setKlad((k) => ({ ...k, [h.id]: { ...(k[h.id] ?? vanServer(h)), ...deel } }));

  /** Wat er straks weg te schrijven valt: alleen regels die echt anders zijn. */
  const gewijzigd = hulpvragen.flatMap((h) => {
    const v = klad[h.id];
    if (!v) return [];
    const anders =
      v.onderwerp !== h.onderwerpNaam ||
      v.deelonderwerp !== h.deelonderwerpNaam ||
      v.vraag !== h.vraag;
    return anders ? [{ hulpvraagId: h.id, ...v }] : [];
  });
  // Een regel waar niets in staat is geen invoer maar een lege regel: die gaat
  // bij het vaststellen gewoon van tafel. Half ingevuld houdt de poort wel
  // tegen, want dan is er iets bedoeld.
  const aangeraakt = nieuwe.filter(
    (n) => n.velden.onderwerp.trim() || n.velden.deelonderwerp.trim() || n.velden.vraag.trim(),
  );
  const nieuweCompleet = aangeraakt.filter((n) => compleet(n.velden));
  const onvolledig =
    hulpvragen.some((h) => !compleet(velden(h))) || aangeraakt.length !== nieuweCompleet.length;
  const totaal = hulpvragen.length + nieuweCompleet.length;

  const zetNieuw = (sleutel: number, deel: Partial<VraagVelden>) =>
    setNieuwe((r) =>
      r.map((n) => (n.sleutel === sleutel ? { ...n, velden: { ...n.velden, ...deel } } : n)),
    );

  const bezigMetBewaren = voegToe.isPending || werkBij.isPending || stelVast.isPending;

  /** Elke hulpvraag krijgt zijn eigen sleutel, ook een die er nog niet is. */
  const lidSleutel = (lid: Groep['leden'][number]) =>
    lid.soort === 'gevonden' ? lid.hulpvraag.id : `nieuw-${lid.sleutel}`;

  /** Het onderwerp van een hele groep omzetten: alle leden mee, van beide
   *  soorten. Anders blijft een zelf toegevoegde regel achter op de oude naam en
   *  valt hij bij het volgende groeperen uit de kaart. */
  const zetGroepnaam = (groep: Groep, naam: string) => {
    // Bestaat dit onderwerp al onder een andere spelling, dan die gebruiken. Zo
    // wordt "p&id" hier "P&ID", zie je dat het is samengevoegd, en staat er
    // straks niet twee keer bijna hetzelfde in de taxonomie.
    const definitief = bestaandeSpelling(naam, bekend) ?? naam;
    for (const lid of groep.leden) {
      if (lid.soort === 'gevonden') zet(lid.hulpvraag, { onderwerp: definitief });
      else zetNieuw(lid.sleutel, { onderwerp: definitief });
    }
  };

  /** Een lege hulpvraag onder een onderwerp. Zonder naam wordt het zijn eigen
   *  kaart; met de naam van een groep komt hij daarin te staan. */
  const voegRegelToe = (onderwerp: string) => {
    teller.current += 1;
    setNieuwe((r) => [
      ...r,
      { sleutel: teller.current, velden: { onderwerp, deelonderwerp: '', vraag: '' } },
    ]);
  };

  if (bezig && hulpvragen.length === 0) return <BezigMetZoeken />;
  if (status === 'FAILED') {
    return (
      <Alert variant="destructive">
        <AlertTitle>{leesbareFout(gesprek?.meeting.failureReason)}</AlertTitle>
      </Alert>
    );
  }
  if (isLoading) return <Skeleton className="h-96 w-full" />;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center gap-3">
        {/* Geen eigen kop: het aantal zegt al wat dit scherm is en waar je
            staat. */}
        <span className="text-sm text-muted-foreground tabular-nums">
          {hulpvragen.length} {hulpvragen.length === 1 ? 'hulpvraag' : 'hulpvragen'} uit{' '}
          {groepen.length} {groepen.length === 1 ? 'onderwerp' : 'onderwerpen'}
        </span>
        {!alVastgesteld && (
          <Button
            className="ml-auto"
            disabled={zoek.isPending}
            onClick={() => zoek.mutate()}
            size="sm"
            variant="outline"
          >
            {zoek.isPending ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <SearchIcon data-icon="inline-start" />
            )}
            Opnieuw zoeken
          </Button>
        )}
      </div>

      {alVastgesteld && (
        <Alert>
          <CheckIcon />
          <AlertTitle>Deze hulpvragen zijn vastgesteld.</AlertTitle>
        </Alert>
      )}
      {fout && (
        <Alert variant="destructive">
          <AlertTitle>{fout}</AlertTitle>
        </Alert>
      )}

      {data?.samenvatting && (
        <Collapsible onOpenChange={setSamenvattingOpen} open={samenvattingOpen}>
          <Card size="sm">
            <CardHeader>
              <CollapsibleTrigger asChild>
                <Button className="group/kop -ml-2 w-fit justify-start" variant="ghost">
                  <ChevronRightIcon className="transition-transform group-data-[state=open]/kop:rotate-90" />
                  Samenvatting van het gesprek
                </Button>
              </CollapsibleTrigger>
            </CardHeader>
            <CollapsibleContent>
              <CardContent className="text-sm whitespace-pre-wrap text-muted-foreground">
                {data.samenvatting}
              </CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      )}

      {hulpvragen.length === 0 && nieuwe.length === 0 && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>Er zijn geen hulpvragen gevonden.</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}

      {groepen.map((groep) => (
        <Card key={groep.sleutel}>
          <CardHeader>
            {alVastgesteld ? (
              <CardTitle>{groep.naam}</CardTitle>
            ) : (
              // Het onderwerp staat één keer boven de groep, dus het wijzigt ook
              // in één keer voor alles eronder. "Cloud" drie keer overtypen is
              // geen correctie maar invoerwerk.
              <OnderwerpVeld
                bekend={bekend}
                label={groep.naam ? `Hoofdonderwerp van "${groep.naam}"` : 'Hoofdonderwerp'}
                onKlaar={(naam) => zetGroepnaam(groep, naam)}
                waarde={groep.naam}
              />
            )}
            {groep.nieuwOnderwerp && (
              <CardAction>
                <Badge variant="secondary">nieuw onderwerp</Badge>
              </CardAction>
            )}
          </CardHeader>
          <CardContent>
            <ItemGroup>
              {groep.leden.map((lid) => (
                <Regel
                  eigen={lid.soort === 'nieuw' || lid.hulpvraag.bron === 'buddy'}
                  key={lidSleutel(lid)}
                  namen={data?.namen ?? []}
                  onVerwijder={() => {
                    if (lid.soort === 'nieuw') {
                      setNieuwe((r) => r.filter((x) => x.sleutel !== lid.sleutel));
                      return;
                    }
                    const { hulpvraag } = lid;
                    setKlad(({ [hulpvraag.id]: _weg, ...rest }) => rest);
                    verwijder.mutate(hulpvraag.id);
                  }}
                  vast={alVastgesteld}
                  waarden={lid.velden}
                  zet={(deel) =>
                    lid.soort === 'gevonden'
                      ? zet(lid.hulpvraag, deel)
                      : zetNieuw(lid.sleutel, deel)
                  }
                />
              ))}
            </ItemGroup>
          </CardContent>
          {!alVastgesteld && (
            <CardFooter>
              {/* Een hulpvraag bij dít onderwerp. Hij krijgt de naam van de groep
                  mee, dus hij verschijnt onderaan deze kaart en niet als losse
                  kaart eronder. */}
              <Button onClick={() => voegRegelToe(groep.naam)} size="sm" variant="ghost">
                <PlusIcon data-icon="inline-start" />
                Hulpvraag bij dit onderwerp
              </Button>
            </CardFooter>
          )}
        </Card>
      ))}

      {!alVastgesteld && (
        <>
          <Button className="w-fit" onClick={() => voegRegelToe('')} variant="outline">
            <PlusIcon data-icon="inline-start" />
            Hulpvraag onder een nieuw onderwerp
          </Button>

          <Card>
            <CardContent className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                Na vaststellen wordt de opname verwijderd. De hulpvragen tellen mee in het
                overzicht.
              </p>
              <Button
                // Nul mag, als er niets is gevonden: ook dat is een uitkomst.
                disabled={(totaal === 0 && hulpvragen.length > 0) || onvolledig || bezigMetBewaren}
                onClick={async () => {
                  setFout('');
                  try {
                    // Eerst het klad, dan de poort. Andersom zou het vaststellen
                    // de oude tekst vastzetten, en daarna weigert de server de
                    // wijziging terecht: een vastgestelde hulpvraag hangt aan de
                    // taxonomie.
                    for (const n of nieuweCompleet) await voegToe.mutateAsync(n.velden);
                    for (const w of gewijzigd) await werkBij.mutateAsync(w);
                    setNieuwe([]);
                    await stelVast.mutateAsync();
                    navigate('/gesprekken');
                  } catch (err) {
                    setFout(err instanceof ApiError ? err.message : String(err));
                  }
                }}
              >
                {bezigMetBewaren && <Spinner data-icon="inline-start" />}
                {bezigMetBewaren
                  ? 'Bezig…'
                  : totaal === 0
                    ? 'Afronden zonder hulpvragen'
                    : `${totaal} hulpvragen vaststellen`}
              </Button>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

/**
 * Een veld dat eruitziet als de tekst die erin staat.
 *
 * Hier stonden gewone velden, allemaal met dezelfde rand. Een kaart met vier
 * hulpvragen was daarmee negen identieke vakjes: je zag niet welk vak het
 * hoofdonderwerp was, welk het deelonderwerp, en welke twee bij elkaar hoorden.
 * Alles had hetzelfde gewicht, dus niets had rangorde.
 *
 * Nu draagt de typografie het, net als in de vastgestelde toestand: het
 * hoofdonderwerp is de kop van de kaart, het deelonderwerp de titel van de
 * hulpvraag, en de vraag de tekst eronder. De rand komt pas als je erover gaat
 * of erin staat, en tot die tijd zie je wat er staat in plaats van waar je het
 * kunt intypen.
 *
 * Dit overschrijft de kleuren van `Input` en `Textarea`, en dat is precies wat
 * de shadcn-regels afraden. Er is geen `ghost`-variant voor invoervelden, en het
 * alternatief is een scherm waarop je de niveaus niet ziet. Daarom staat het hier
 * één keer en niet verspreid over de velden.
 */
const ONZICHTBAAR =
  'border-transparent bg-transparent px-2 hover:border-input focus-visible:bg-background dark:bg-transparent dark:hover:border-input';

/** De drie velden zoals ze op de server staan. */
const vanServer = (h: HulpvraagRij): VraagVelden => ({
  onderwerp: h.onderwerpNaam,
  deelonderwerp: h.deelonderwerpNaam,
  vraag: h.vraag,
});

/**
 * Het hoofdonderwerp van een groep, met suggesties pas terwijl je typt.
 *
 * De volledige lijst is geen keuzemenu: hij groeit met elk gesprek, en een
 * dropdown die bij het aanklikken tweehonderd namen opengooit helpt niemand.
 * Zonder iets getypt krijgt het veld daarom geen `list`, en daarmee laat de
 * browser hem dicht. Dat is de reden dat hier een `datalist` staat en geen
 * `Combobox`: die laatste is een popover die je opent, en dat is precies wat hier
 * niet moet. Een exacte treffer valt af, anders staat er bij een naam die al
 * klopt een suggestie die hetzelfde zegt.
 *
 * Wat je typt blijft lokaal tot je het veld verlaat, en pas dán gaat het naar
 * het klad. Dat is nodig sinds de groepering op die naam loopt: per toetsaanslag
 * doorgeven betekent dat de kaart bij "P" al samenvalt met een ander onderwerp
 * dat met een p begint, en dan verspringt de hele groep onder je handen terwijl
 * je nog aan het typen bent.
 */
function OnderwerpVeld({
  waarde,
  label,
  bekend,
  onKlaar,
}: {
  waarde: string;
  label: string;
  bekend: readonly string[];
  onKlaar: (naam: string) => void;
}) {
  const [getypt, setGetypt] = useState<string | null>(null);
  const zichtbaar = getypt ?? waarde;
  const zoek = zichtbaar.trim().toLowerCase();
  const suggesties = zoek
    ? bekend.filter((n) => n.toLowerCase().includes(zoek) && n.toLowerCase() !== zoek).slice(0, 8)
    : [];
  const lijst = `onderwerpen-${useId()}`;

  const afronden = () => {
    setGetypt(null);
    if (getypt !== null && getypt !== waarde) onKlaar(getypt);
  };

  return (
    <>
      <Input
        aria-label={label}
        className={cn(ONZICHTBAAR, 'h-9 font-heading text-lg font-medium')}
        list={suggesties.length > 0 ? lijst : undefined}
        onBlur={afronden}
        onChange={(e) => setGetypt(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') setGetypt(null);
        }}
        placeholder="Hoofdonderwerp"
        value={zichtbaar}
      />
      {suggesties.length > 0 && (
        <datalist id={lijst}>
          {suggesties.map((n) => (
            <option key={n} value={n} />
          ))}
        </datalist>
      )}
    </>
  );
}

const compleet = (v: VraagVelden) =>
  Boolean(v.onderwerp.trim() && v.deelonderwerp.trim() && v.vraag.trim());

/**
 * Eén hulpvraag: twee velden die eruitzien als de tekst die er stond.
 *
 * Geen knop om te gaan bewerken en geen knop om te bewaren. Wat je typt staat in
 * het klad van het scherm en gaat mee bij het vaststellen; dat is één poort in
 * plaats van elf keer bevestigen.
 *
 * Dezelfde regel dient voor een gevonden hulpvraag en voor een lege die de buddy
 * er zelf bij zet. Daarom kent hij het serverrecord niet: er is er nog geen als
 * je net op de plusknop hebt gedrukt.
 *
 * Het vraagveld groeit mee met de tekst via `field-sizing-content`. Hier stond
 * een effect dat `scrollHeight` naar `style.height` schreef; dat doet de browser
 * nu zelf. Meegroeien blijft het punt: een tekstvak met een eigen schuifbalk
 * maakt van lezen bladeren, en dit is de tekst die gelezen moet worden.
 */
/**
 * Eén hulpvraag, met het vangnet tegen namen erin.
 *
 * De hulpvraag reist verder dan dit gesprek: hij komt in het bewijspaneel dat
 * elke buddy en elke Admin leest. De prompt vraagt het model om namen weg te
 * laten, en dit is wat er overblijft als het model zich er niet aan houdt.
 *
 * Waarschuwen en niet tegenhouden: "Bas" en "Wil" zijn ook gewone woorden, en
 * een buddy die niet verder kan door een valse melding is erger dan een naam die
 * één keer doorglipt. De controle loopt over de tekst zoals hij nu op het scherm
 * staat, dus de melding verdwijnt terwijl hij hem wegtypt.
 */
function Regel({
  waarden,
  zet,
  vast,
  namen,
  onVerwijder,
  eigen = false,
}: {
  waarden: VraagVelden;
  zet: (deel: Partial<VraagVelden>) => void;
  vast: boolean;
  /** Losse woorden uit de namen die de administratie kent. */
  namen: string[];
  onVerwijder: () => void;
  /** Door de buddy zelf neergezet en niet door het model gevonden. */
  eigen?: boolean;
}) {
  const gevonden = vindNamen(
    `${waarden.onderwerp} ${waarden.deelonderwerp} ${waarden.vraag}`,
    namen,
  );
  if (vast) {
    return (
      <Item variant="muted">
        <ItemContent>
          <ItemTitle>{waarden.deelonderwerp}</ItemTitle>
          <ItemDescription className="whitespace-pre-wrap">{waarden.vraag}</ItemDescription>
        </ItemContent>
        {eigen && (
          <ItemActions className="self-start">
            <Badge variant="outline">zelf toegevoegd</Badge>
          </ItemActions>
        )}
      </Item>
    );
  }

  return (
    <Item variant="muted">
      <ItemContent>
        {/* Dezelfde twee lagen als in de vastgestelde toestand hierboven: het
            deelonderwerp is de titel van deze hulpvraag, de vraag is de tekst
            eronder. Alleen bewerkbaar. */}
        <Input
          aria-label="Deelonderwerp"
          className={cn(ONZICHTBAAR, 'font-medium')}
          onChange={(e) => zet({ deelonderwerp: e.target.value })}
          placeholder="Deelonderwerp"
          value={waarden.deelonderwerp}
        />
        <Textarea
          aria-label="De vraag"
          // Gedempt zoals de `ItemDescription` in de vastgestelde toestand, zodat
          // de twee regels ook op kleur van elkaar te onderscheiden zijn en niet
          // alleen op gewicht. Vol contrast zodra je erin staat, want gedempte
          // tekst zit niet fijn om in te typen.
          className={cn(
            ONZICHTBAAR,
            'min-h-0 py-1 text-muted-foreground focus-visible:text-foreground',
          )}
          onChange={(e) => zet({ vraag: e.target.value })}
          placeholder="De vraag zelf"
          rows={1}
          value={waarden.vraag}
        />
        {gevonden.length > 0 && (
          <p className="text-destructive flex items-center gap-1.5 text-sm">
            <AlertTriangleIcon className="size-3.5 shrink-0" />
            {gevonden.length === 1 ? 'Naam in de tekst' : 'Namen in de tekst'}:{' '}
            {gevonden.join(', ')}
          </p>
        )}
      </ItemContent>
      <ItemActions className="self-start">
        {eigen && <Badge variant="outline">zelf toegevoegd</Badge>}
        <Button
          aria-label={
            waarden.deelonderwerp
              ? `"${waarden.deelonderwerp}" verwijderen`
              : 'Deze hulpvraag verwijderen'
          }
          onClick={onVerwijder}
          size="icon-sm"
          variant="ghost"
        >
          <XIcon />
        </Button>
      </ItemActions>
    </Item>
  );
}
